import { PackageSearch, Truck, MapPin } from "lucide-react";
import { formatINR, formatDate } from "../../lib/api";
import { fromCm, roundN } from "../../lib/units";

const volPerUnit = (it) => roundN((Number(it.length_cm || 0) * Number(it.width_cm || 0) * Number(it.height_cm || 0)) / 5000, 2);

export function Cargo({ lr, lrItems, vehicles }) {
  const totQty = Math.round((lrItems || []).reduce((s, it) => s + Number(it.quantity || 0), 0));
  const totWt = roundN((lrItems || []).reduce((s, it) => s + Number(it.weight_kg || 0), 0), 2);
  const totVol = roundN((lrItems || []).reduce((s, it) => s + volPerUnit(it) * Number(it.quantity || 0), 0), 2);
  return (
    <>
      {lrItems.length > 0 && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-goods">
          <div className="section-title flex items-center gap-2"><PackageSearch className="w-4 h-4" /> Goods / Items <span className="text-[11px] font-normal text-[#68665E]">(dimensions in feet)</span></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr>
                <th className="table-th">Description</th><th className="table-th text-right">Qty</th><th className="table-th">Unit</th>
                <th className="table-th text-right">L (ft)</th><th className="table-th text-right">B (ft)</th><th className="table-th text-right">H (ft)</th><th className="table-th text-right">Weight (kg)</th><th className="table-th text-right">Vol wt (kg)</th>
              </tr></thead>
              <tbody>
                {lrItems.map((it, i) => (
                  <tr key={i} className="border-t border-[#F0EDE5]" data-testid={`lr-detail-item-${i}`}>
                    <td className="table-td">{it.description || "—"}</td>
                    <td className="table-td text-right">{it.quantity || "—"}</td>
                    <td className="table-td">{it.unit || "Nos"}</td>
                    <td className="table-td text-right">{it.length_cm ? roundN(fromCm(it.length_cm, "ft"), 2) : "—"}</td>
                    <td className="table-td text-right">{it.width_cm ? roundN(fromCm(it.width_cm, "ft"), 2) : "—"}</td>
                    <td className="table-td text-right">{it.height_cm ? roundN(fromCm(it.height_cm, "ft"), 2) : "—"}</td>
                    <td className="table-td text-right">{it.weight_kg || "—"}</td>
                    <td className="table-td text-right text-[#68665E]">{roundN(volPerUnit(it) * Number(it.quantity || 0), 2) || "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#E5E3DB] bg-[#FAF9F5] font-semibold">
                  <td className="table-td">Totals</td>
                  <td className="table-td text-right" data-testid="lr-detail-total-packages">{totQty}</td>
                  <td className="table-td" colSpan={4}></td>
                  <td className="table-td text-right" data-testid="lr-detail-total-weight">{totWt}</td>
                  <td className="table-td text-right" data-testid="lr-detail-total-vol">{totVol}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="text-[11px] text-[#68665E] mt-2">Volumetric weight (L×B×H ÷ 5000) is for reference only — the LR prints weight as <strong>FIXED</strong>.</div>
        </div>
      )}

      <div className="bg-white border border-[#E5E3DB] p-4 mb-6">
        <div className="section-title flex items-center gap-2"><Truck className="w-4 h-4" /> Vehicles &amp; Drivers</div>
        {vehicles.length === 0 ? <div className="text-sm text-[#A09E96]">No vehicles assigned.</div> : (
          <div className="divide-y divide-[#E5E3DB]">
            {vehicles.map((v, i) => (
              <div key={i} className="flex flex-wrap items-center gap-4 py-2 text-sm" data-testid={`lr-detail-vehicle-${i}`}>
                <span className="font-mono font-medium w-40">{v.vehicle_number || "—"}</span>
                <span>{v.driver_name || "—"}</span>
                <span className="text-[#68665E]">{v.driver_phone || ""}</span>
                {v.broker_name && <span className="text-[#68665E]">Broker: {v.broker_name}{v.broker_phone ? ` · ${v.broker_phone}` : ""}</span>}
                {(Number(v.lh_advance || 0) + Number(v.lh_balance || 0)) > 0 && (
                  <span className="ml-auto text-[#1A1A18]">LH: {formatINR((Number(v.lh_advance || 0) + Number(v.lh_balance || 0)))} <span className="text-[11px] text-[#68665E]">(Adv {formatINR(v.lh_advance || 0)} {v.lh_advance_paid ? "✓ paid" : ""} · Bal {formatINR(v.lh_balance || 0)})</span></span>
                )}
                {Number(v.lh_balance || 0) > 0 && (
                  <span className={`text-[11px] px-2 py-0.5 rounded ${v.lh_balance_paid ? "bg-[#E3F0E3] text-[#2F6F4E]" : "bg-[#FBE9E7] text-[#A0522D]"}`} data-testid={`lr-detail-lh-balance-status-${i}`}>
                    {v.lh_balance_paid ? `Balance paid${v.lh_paid_date ? ` · ${formatDate(v.lh_paid_date)}` : ""}` : "Balance not paid"}
                  </span>
                )}
              </div>
            ))}
            {vehicles.reduce((s, v) => s + Number(v.lh_advance || 0) + Number(v.lh_balance || 0), 0) > 0 && (
              <div className="flex justify-end py-2 text-sm font-semibold" data-testid="lr-detail-lh-total">Total Lorry Hire: {formatINR(vehicles.reduce((s, v) => s + Number(v.lh_advance || 0) + Number(v.lh_balance || 0), 0))}</div>
            )}
          </div>
        )}
        {(lr.pickup_maps_url || lr.delivery_maps_url) && (
          <div className="flex gap-4 mt-3 text-sm">
            {lr.pickup_maps_url && <a className="text-[#C84B31] hover:underline inline-flex items-center gap-1" href={lr.pickup_maps_url} target="_blank" rel="noreferrer"><MapPin className="w-3 h-3" /> Pickup</a>}
            {lr.delivery_maps_url && <a className="text-[#C84B31] hover:underline inline-flex items-center gap-1" href={lr.delivery_maps_url} target="_blank" rel="noreferrer"><MapPin className="w-3 h-3" /> Delivery</a>}
          </div>
        )}
      </div>
    </>
  );
}
